<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import VideoVimeo from "$lib/molecules/VideoVimeo.svelte";

  // Element references
  let progressBars: HTMLElement[] = [];
  let videos: HTMLElement[] = [];

  // Initialize state
  let activeVideoIndex = 0;

  // Get the block data
  export let blok;

  // Handles navigation clicks
  function handleNavClick(videoIndex: number) {
    activeVideoIndex = videoIndex;
  }
</script>

<section class="video-carousel-section section-header-width mx-auto" use:storyblokEditable={blok}>
  <div class="heading">
    <h1 class="main-heading">{blok.heading}</h1>
    <h2 class="main-subheading">{blok.subheading}</h2>
  </div>
  {#if blok.videos.length > 1}
    <div class="navigation">
      {#each blok.videos as video, i}
        <div
          class="navigation-item"
          role="button"
          tabindex="0"
          on:click={() => handleNavClick(i)}
          on:keydown={(e) => e.key === "Enter" && handleNavClick(i)}
          class:active={i === activeVideoIndex}
        >
          <div
            class="progress-bar"
            class:active={i === activeVideoIndex}
            bind:this={progressBars[i]}
          ></div>

          <div class="video-description" class:active={i === activeVideoIndex}>
            <p class="video-duration">{video.duration}</p>
            <h3 class="video-title">{video.title}</h3>
          </div>
        </div>
      {/each}
    </div>
  {/if}
  <div class="videos">
    {#each blok.videos as video, i}
      <div class="video" bind:this={videos[i]} class:active={i === activeVideoIndex}>
        <VideoVimeo blok={video} />
      </div>
    {/each}
  </div>
</section>

<style lang="postcss">
  .video-carousel-section {
    padding-top: 4rem;
    padding-bottom: 2rem;
    display: grid;
    grid-template-areas:
      "heading"
      "video"
      "navigation";
    grid-template-columns: 1fr;
    gap: 2rem;

    @screen lg {
      padding-top: 12rem;
      grid-template-columns: 35% 65%;
      grid-template-areas:
        "heading video"
        "navigation video";
    }
  }

  .heading {
    grid-area: heading;
  }

  .navigation {
    grid-area: navigation;
  }

  .videos {
    display: grid;
    grid-template-columns: 1fr;
    grid-area: video;
  }

  .video {
    grid-row: 1;
    grid-column: 1;
    opacity: 0;
    transition:
      opacity 0.3s ease,
      transform 0.5s ease;
    z-index: 0;

    @screen lg {
      transform: translateY(20px);
    }

    &.active {
      opacity: 1;
      z-index: 10;

      @screen lg {
        transform: translateY(0px);
      }
    }
  }

  .navigation-item {
    display: flex;
    flex-direction: row;
    margin-bottom: 2px;
    height: 30px;
    cursor: pointer;

    @screen lg {
      height: 36px;
    }

    &.active {
      height: 56px;

      @screen lg {
        height: 66px;
      }
    }

    &:hover {
      .video-title {
        color: #fff;
      }
    }
  }

  .progress-bar {
    width: 2px;
    height: 24px;
    border-radius: 2px;
    background-color: #54b3df;
    transition:
      height 0.3s ease,
      background-color 0.3s ease;

    @screen lg {
      height: 28px;
    }

    &.active {
      height: 48px;
      background-color: #fff;

      @screen lg {
        height: 58px;
      }
    }
  }

  .video-description {
    margin-left: 1rem;

    .video-duration {
      display: none;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.6;
      color: #54b3df;
    }

    .video-title {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.6;
      color: #54b3df;
      white-space: nowrap;
      transition: color 0.3s ease;

      @screen lg {
        font-size: 18px;
      }
    }

    &.active {
      .video-duration {
        display: block;
        @screen lg {
          font-size: 14px;
        }
      }

      .video-title {
        font-size: 18px;
        color: #ffffff;

        @screen lg {
          font-size: 24px;
        }
      }
    }
  }

  .main-heading {
    font-size: 38px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1.1;
    color: #fff;
    white-space: pre-wrap;

    @screen lg {
      font-size: 48px;
    }
  }

  .main-subheading {
    margin-top: 1.5rem;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.6;
    color: #fff;
    white-space: pre-wrap;

    @screen lg {
      font-size: 18px;
    }
  }
</style>
